import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import '../../styles/styles.css';
import '../../styles/custom-fixes.css';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import {
  FILE_DOWNLOAD_PREFIX,
  APP_URLS,
  getBlogDetailUrl,
} from '../../helpers/url_helper';
import { SITE_URL, canonicalUrl } from '../../seo/publicPages';
import SiteHeader from '../../components/SiteHeader';
import Footer from './Footer';
import ContactUsForm from '../ContactUs/ContactUsForm';
import useSeoMeta from '../SeoPages/useSeoMeta';
import './Blog.css';

const getBlogImageUrl = (path) => {
  if (!path) return null;
  const value = String(path).trim();
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  const encoded = value
    .replace(/^\/+/, '')
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `${FILE_DOWNLOAD_PREFIX}${encoded}`;
};

const formatBlogDate = (isoDate, options = {}) => {
  if (!isoDate) return '';
  try {
    return new Date(isoDate).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      ...options,
    });
  } catch {
    return '';
  }
};

const isWeakMeta = (value) => {
  if (!value || !String(value).trim()) return true;
  const text = String(value).trim().toLowerCase();
  return text.startsWith('lorem') || text === 'lorem speum' || text.length < 8;
};

const stripHtml = (html = '') =>
  String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Prefer API meta_title; fall back to article title for SEO. */
const buildSeoTitle = (post) => {
  const metaTitle = post.meta_title?.trim();
  if (metaTitle && !isWeakMeta(metaTitle)) return metaTitle;
  const title = post.title?.trim();
  return title ? `${title} | Complimate Blog` : 'Blog | Complimate';
};

/** Prefer API meta_description; fall back to body excerpt. */
const buildSeoDescription = (post) => {
  const metaDescription = post.meta_description?.trim();
  if (metaDescription && !isWeakMeta(metaDescription)) return metaDescription;
  const fromBody = stripHtml(post.description);
  if (fromBody) {
    return fromBody.length > 160 ? `${fromBody.slice(0, 157).trim()}…` : fromBody;
  }
  if (post.title) return `${post.title} — insights from Complimate WHS & compliance software.`;
  return 'Complimate blog article';
};

/** Use API schema JSON when valid; otherwise null. */
const parseApiSchema = (schemaValue) => {
  if (!schemaValue || isWeakMeta(schemaValue)) return null;
  if (typeof schemaValue === 'object') return schemaValue;
  try {
    const parsed = JSON.parse(String(schemaValue));
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    return null;
  }
  return null;
};

const parseRgbChannels = (colorValue = '') => {
  const match = String(colorValue).match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!match) return null;
  return {
    r: Number(match[1]),
    g: Number(match[2]),
    b: Number(match[3]),
  };
};

const isLightBackground = (colorValue = '') => {
  const value = String(colorValue).trim().toLowerCase();
  if (!value || value === 'transparent' || value === 'rgba(0, 0, 0, 0)') return false;
  if (value === 'white' || value === '#fff' || value === '#ffffff') return true;

  const rgb = parseRgbChannels(value);
  if (!rgb) return false;

  // Perceived luminance – treat near-white/light greys as light backgrounds
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance >= 0.75;
};

function BlogDetail() {
  const { mode } = useThemeMode();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const contentRef = useRef(null);

  const heroImage = getBlogImageUrl(post?.article_image);
  const ogImage = heroImage || getBlogImageUrl(post?.article_thumbnail);
  const detailPath = slug ? getBlogDetailUrl(slug) : APP_URLS.BLOG;
  const siteBase = SITE_URL || 'https://complimate.com.au';

  const seoTitle = post ? buildSeoTitle(post) : 'Blog | Complimate';
  const seoDescription = post ? buildSeoDescription(post) : 'Complimate blog article';

  const articleJsonLd = useMemo(() => {
    if (!post) return null;
    const url = canonicalUrl(detailPath);
    const imageUrl = ogImage || undefined;
    const authorName = post.author || post.created_by || 'Complimate';
    const apiSchema = parseApiSchema(post.schema);

    const breadcrumb = {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteBase}/` },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteBase}/blog/` },
        { '@type': 'ListItem', position: 3, name: post.title, item: url },
      ],
    };

    const blogPosting = {
      '@type': 'BlogPosting',
      headline: post.title,
      alternativeHeadline: post.sub_title || undefined,
      description: seoDescription,
      image: imageUrl ? [imageUrl] : undefined,
      datePublished: post.created_at,
      dateModified: post.updated_at || post.created_at,
      author: {
        '@type': 'Organization',
        name: authorName,
      },
      publisher: {
        '@type': 'Organization',
        name: 'Complimate',
        logo: {
          '@type': 'ImageObject',
          url: `${siteBase}/static/media/logo.a55ca2e3df2aeb710b3f953ac71d4d05.svg`,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': url,
      },
      articleSection: post.category?.name || 'Complimate',
      keywords: post.category?.name || undefined,
      inLanguage: 'en-AU',
      url,
    };

    // Prefer valid CMS schema when provided; always keep breadcrumbs for SEO.
    if (apiSchema) {
      return {
        '@context': 'https://schema.org',
        '@graph': [breadcrumb, apiSchema],
      };
    }

    return {
      '@context': 'https://schema.org',
      '@graph': [breadcrumb, blogPosting],
    };
  }, [post, detailPath, ogImage, seoDescription, siteBase]);

  useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    path: detailPath,
    ogType: 'article',
    ogImage: ogImage || undefined,
    jsonLd: articleJsonLd || undefined,
    article: post
      ? {
          publishedTime: post.created_at,
          modifiedTime: post.updated_at || post.created_at,
          author: post.author || post.created_by || 'Complimate',
          section: post.category?.name || 'Complimate',
        }
      : undefined,
  });

  useEffect(() => {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(mode === 'dark' ? 'dark-mode' : 'light-mode');
  }, [mode]);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    const cells = root.querySelectorAll('th, td');
    cells.forEach((cell) => {
      cell.classList.remove('blog-cell-light-bg');
      if (mode !== 'dark') return;

      const bg = window.getComputedStyle(cell).backgroundColor;
      if (isLightBackground(bg)) {
        cell.classList.add('blog-cell-light-bg');
      }
    });
  }, [mode, post?.description, loading]);

  useEffect(() => {
    let cancelled = false;

    const fetchPost = async () => {
      if (!slug) {
        setError('Blog post not found.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await dataAPI.getBlogBySlug(slug, { showLoading: true, loadingMessage: 'Loading article...' });
        if (cancelled) return;

        if (response.status === 'success' && response.data) {
          setPost(response.data);
        } else {
          setError('Blog post not found.');
        }
      } catch (err) {
        console.error('Error fetching blog post:', err);
        if (!cancelled) setError('Failed to load this blog post.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPost();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const enquiryDescription = post
    ? `Hi Complimate team, I read your article "${post.title}" and would like to learn more about Complimate.`
    : '';

  const shortDescription = useMemo(() => {
    if (!post) return '';
    if (post.sub_title && String(post.sub_title).trim()) return String(post.sub_title).trim();
    const plain = stripHtml(post.description);
    if (!plain) return '';
    return plain.length > 140 ? `${plain.slice(0, 140).trim()}…` : plain;
  }, [post]);

  const readingMinutes = useMemo(() => {
    if (!post?.description) return 1;
    const words = stripHtml(post.description).split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.ceil(words / 200));
  }, [post]);

  return (
    <div
      className={`blog-detail-page ${mode === 'dark' ? 'blog-detail-page-dark' : ''}`}
      style={{
        backgroundColor: mode === 'dark' ? '#111' : '#fff',
        minHeight: '100vh',
      }}
    >
      <SiteHeader activeNav="blog" />

      <main className={`blog-section ${mode === 'dark' ? 'blog-section-dark' : ''}`}>
        {loading && (
          <Container className="py-5">
            <div className="blog-skeleton blog-skeleton-detail" aria-busy="true" />
          </Container>
        )}

        {error && !loading && (
          <Container className="py-5">
            <nav className="blog-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <Link to={APP_URLS.BLOG}>Blog</Link>
            </nav>
            <p className="blog-status blog-status-error text-center">{error}</p>
            <p className="text-center">
              <Link to={APP_URLS.BLOG} className="blog-read-link">
                Browse all articles
              </Link>
            </p>
          </Container>
        )}

        {!loading && !error && post && (
          <>
            <header className="blog-detail-banner">
              <Container>
                <nav className="blog-breadcrumb blog-breadcrumb-on-banner" aria-label="Breadcrumb">
                  <ol>
                    <li><Link to="/">Home</Link></li>
                    <li><Link to={APP_URLS.BLOG}>Blog</Link></li>
                    <li aria-current="page">
                      <span>{post.title}</span>
                    </li>
                  </ol>
                </nav>

                <Row className="align-items-center g-4 blog-detail-banner-row">
                  <Col xs={12} lg={6}>
                    <figure className="blog-detail-banner-media">
                      {heroImage ? (
                        <img
                          src={heroImage}
                          alt={post.title}
                          className="blog-detail-banner-img"
                          fetchPriority="high"
                        />
                      ) : (
                        <div className="blog-detail-banner-fallback" aria-hidden="true">
                          Complimate
                        </div>
                      )}
                    </figure>
                  </Col>
                  <Col xs={12} lg={6}>
                    <div className="blog-detail-banner-copy">
                      {post.category?.name && (
                        <span className="blog-detail-banner-badge">{post.category.name}</span>
                      )}
                      <h1 className="blog-detail-banner-title">{post.title}</h1>
                      {shortDescription ? (
                        <p className="blog-detail-banner-desc">{shortDescription}</p>
                      ) : null}
                      <div className="blog-detail-banner-meta">
                        <span className="blog-detail-banner-author-avatar" aria-hidden="true">
                          {(post.author || post.created_by || 'C').charAt(0).toUpperCase()}
                        </span>
                        <div className="blog-detail-banner-meta-text">
                          <span>
                            By {post.author || post.created_by || 'Complimate'}
                          </span>
                          <span className="blog-detail-banner-meta-sep" aria-hidden="true">•</span>
                          <time dateTime={post.created_at}>{formatBlogDate(post.created_at)}</time>
                          <span className="blog-detail-banner-meta-sep" aria-hidden="true">•</span>
                          <span>{readingMinutes} min read</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Container>
            </header>

            <Container className="blog-detail-layout pb-5">
              <Row className="g-4 g-xl-5">
                <Col xs={12} lg={8}>
                  <article
                    className="blog-detail-article"
                    itemScope
                    itemType="https://schema.org/BlogPosting"
                  >
                    <meta itemProp="headline" content={post.title} />
                    <meta itemProp="datePublished" content={post.created_at} />
                    {post.updated_at && (
                      <meta itemProp="dateModified" content={post.updated_at} />
                    )}

                    <div
                      ref={contentRef}
                      className="blog-detail-content"
                      itemProp="articleBody"
                      dangerouslySetInnerHTML={{ __html: post.description || '' }}
                    />

                    <footer className="blog-detail-footer">
                      <div className="blog-detail-tags">
                        {post.category?.name && (
                          <span className="blog-category">{post.category.name}</span>
                        )}
                        <span className="blog-detail-updated">
                          Updated {formatBlogDate(post.updated_at || post.created_at)}
                        </span>
                      </div>
                      <div className="blog-detail-share-row">
                        <Link to={APP_URLS.BLOG} className="blog-back-link">
                          ← All articles
                        </Link>
                        <Link to="/contact-us/" className="blog-read-link">
                          Contact Complimate
                        </Link>
                      </div>
                    </footer>
                  </article>

                  <aside className="blog-detail-cta-band" aria-label="Talk to Complimate">
                    <div>
                      <h2 className="blog-cta-title">Ready to simplify WHS compliance?</h2>
                      <p className="blog-cta-text">
                        See how Complimate helps Australian teams manage audits, forms, policies and safety records in one place.
                      </p>
                    </div>
                    <div className="blog-cta-actions">
                      <Link to="/contact-us/" className="blog-cta-btn blog-cta-btn-primary">
                        Request a demo
                      </Link>
                      <Link to="/learning-hub/" className="blog-cta-btn blog-cta-btn-ghost">
                        Visit Learning Hub
                      </Link>
                    </div>
                  </aside>
                </Col>

                <Col xs={12} lg={4}>
                  <aside className="blog-enquiry-panel" id="blog-enquiry">
                    <div className="blog-enquiry-card">
                      <p className="blog-enquiry-eyebrow">Talk with our team</p>
                      <h2 className="blog-enquiry-title">Have a question after reading?</h2>
                      <p className="blog-enquiry-copy">
                        Send an enquiry and we&apos;ll help you explore Complimate for your workplace.
                      </p>
                      <ContactUsForm
                        defaultCategory="enterprise"
                        defaultDescription={enquiryDescription}
                        submitLabel="Send enquiry"
                        submitButtonId="blog_detail_enquiry_submit"
                        embedded
                      />
                      <p className="blog-enquiry-footnote">
                        Prefer email?{' '}
                        <a href="mailto:support@complimate.com.au">support@complimate.com.au</a>
                      </p>
                    </div>
                  </aside>
                </Col>
              </Row>
            </Container>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default BlogDetail;
